import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import type { AssistantIdentity } from "@/types/contacts";
import {
  ALEX_AVATARS,
  ALEX_DEFAULT_AVATAR_TOKEN,
  isAlexAvatarToken,
} from "@/components/agent/assistant-avatars/AlexAvatars";
import {
  assistantDisplayName,
  assistantInitials,
  isDefaultAssistantIdentity,
} from "@/hooks/useAssistantIdentity";

/**
 * `AssistantAvatar` — the assistant's face, resolved from the §6.2 identity
 * record. ONE resolver so the hero, the slim strip and (later) the chat header
 * paint the same picture. Display-only; the picker that WRITES `identity.avatar`
 * is S3.
 *
 * Resolution (C1 — `avatar` is a ≤500-char token/URL, never a data-URI):
 *   1. builtin token (`builtin:alex-*`) → the bundled Alex SVG.
 *   2. http(s)/data URL → `<img>`; a broken load falls to (4), the initials
 *      roundel — so a lawyer who pasted a dead URL SEES it is broken.
 *   3. emoji (separate `emoji` field, avatar unset) → tinted roundel.
 *   4. nothing set → Alex's default avatar (never a blank circle).
 * The circle is never empty: unset → Alex default; broken URL → initials.
 *
 * The plan's fallback chain also names an ORG-default step (§3.3 / §5) between
 * the user's avatar and Alex's default. It is deferred here on purpose: S1's
 * `identity.get` surface carries no org-config field, and the org default is
 * materialized server-side into `identity.avatar` (or picked via the S3 dialog's
 * "org default" option) — so it flows through branch 1/2 above, not a separate
 * client lookup. Revisit if a client-visible org identity is ever exposed.
 */
export function AssistantAvatar({
  identity,
  size = 48,
  className,
  testid,
}: {
  identity: AssistantIdentity | null;
  size?: number;
  className?: string;
  testid?: string;
}) {
  const avatar = identity?.avatar?.trim();
  const emoji = identity?.emoji?.trim();

  const [imgFailed, setImgFailed] = useState(false);
  // A new URL deserves a fresh load attempt — clear the prior failure.
  useEffect(() => setImgFailed(false), [avatar]);

  const wrap = "shrink-0 overflow-hidden rounded-full";

  // 1. builtin token → bundled Alex art.
  if (isAlexAvatarToken(avatar)) {
    const Art = ALEX_AVATARS[avatar];
    return <Art size={size} className={cn(wrap, className)} testid={testid} />;
  }

  // 2. custom URL → <img>, falling to initials on error.
  const isUrl = !!avatar && /^(https?:|data:)/i.test(avatar);
  if (isUrl && !imgFailed) {
    return (
      <img
        src={avatar}
        alt=""
        width={size}
        height={size}
        data-testid={testid}
        onError={() => setImgFailed(true)}
        className={cn(wrap, "object-cover", className)}
        style={{ width: size, height: size }}
      />
    );
  }

  // 4 (error path). Broken custom URL → initials roundel.
  if (isUrl && imgFailed) {
    return (
      <span
        data-testid={testid}
        className={cn(
          wrap,
          "inline-flex items-center justify-center bg-cream-200 font-semibold text-coffee-700 dark:bg-coffee-800 dark:text-cream-200",
          className,
        )}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
        aria-hidden="true"
      >
        {assistantInitials(assistantDisplayName(identity))}
      </span>
    );
  }

  // 2.5. The DEFAULT identity shows Alex's HUMAN portrait, not a symbol
  // (operator 2026-07-27): a setup-seeded emoji on a never-named assistant is
  // not a chosen face, so "Alex" always appears as a person. A lawyer who
  // RENAMES keeps their emoji (branch 3); an explicit avatar wins above.
  if (isDefaultAssistantIdentity(identity)) {
    const Alex = ALEX_AVATARS[ALEX_DEFAULT_AVATAR_TOKEN];
    return <Alex size={size} className={cn(wrap, className)} testid={testid} />;
  }

  // 3. emoji roundel.
  if (emoji) {
    return (
      <span
        data-testid={testid}
        className={cn(
          wrap,
          "inline-flex items-center justify-center bg-gold-400/20 dark:bg-gold-500/20",
          className,
        )}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.5) }}
        aria-hidden="true"
      >
        {emoji}
      </span>
    );
  }

  // 4 (default). Nothing set → Alex's default avatar. Never blank.
  const Default = ALEX_AVATARS[ALEX_DEFAULT_AVATAR_TOKEN];
  return <Default size={size} className={cn(wrap, className)} testid={testid} />;
}
